<template>
  <div
    id="page-wrapper"
    class="wrap-container wrap-container--has-margin-bottom entrance-page"
    role="main"
  >
    <on-page-breadcrumbs :data="breadcrumbs" />
    <hero-block
      v-bind="heroBlock"
      edit-prefix="HeroBlock"
    />

    <navigation-blocks
      v-if="matchingTags.length"
      :navigation-blocks="matchingTags"
      :display-image-state="false"
      :show-more-limit="!isEditable ? ($root.isSmallViewport ? 4 : 4) : 0"
      :show-more-all="false"
    >
      <sub-heading
        v-if="matchingTagsTitle || isEditable"
        :v-edit-heading="'MatchingTagsTitle'"
        :heading="matchingTagsTitle"
      />
    </navigation-blocks>

    <search-box
      search-text="Sök på Ica.se/Inspiration"
      api-url="/api/search/inspiration"
      tracking-title="inspirationssök"
      @searchResult="showSearchResult"
    >
      <sub-heading
        :v-edit-heading="'SearchTitle'"
        :heading="searchFieldTitle"
      />
    </search-box>

    <!-- CONTENT AREA -->
    <section
      v-if="!hasSearched && (contentArea.length || isEditable)"
      class="content-area-wrapper"
    >
      <sub-heading
        v-if="contentAreaTitle || isEditable"
        :v-edit-heading="'ContentAreaTitle'"
        :heading="contentAreaTitle"
      />

      <block-grid
        v-if="contentArea.length || isEditable"
        v-edit="'ContentArea'"
        :blocks="contentArea"
        :custom-size="fixedSizeFullFirstAllSizes"
        :tracking="{
          listName: contentAreaTitle,
          listLength: undefined
        }"
      />
    </section>

    <!-- ARTIKLAR -->
    <section
      v-if="itemLists.articles || isEditable"
      ref="articles"
      class="content-area-wrapper"
    >
      <sub-heading
        v-if="articleBlockTitle || isEditable"
        :v-edit-heading="'ArticleBlockTitle'"
        :heading="articleBlockTitle"
        :count="itemStats.articles"
        anchor-link="articles"
      />

      <block-grid
        :blocks="itemLists.articles"
        :custom-size="fixedSizeFullFirst"
        :force-row-size="'third'"
        :tracking="{
          listName: articleBlockTitle,
          listLength: undefined
        }"
      />

      <show-more
        v-if="moreApiUrls.articles"
        :api-url="moreApiUrls.articles"
        :tracking="{
          event: 'articleShowMore'
        }"
        @showMoreResult="showMoreAddToList('articles', ...arguments)"
      />
    </section>

    <!-- RECEPT -->
    <section
      v-if="(itemLists.recipes && itemLists.recipes.length) || isEditable"
      ref="recipes"
      class="content-area-wrapper"
    >
      <sub-heading
        v-if="recipeBlockTitle || isEditable"
        :v-edit-heading="'RecipeBlockTitle'"
        :heading="recipeBlockTitle"
        :count="itemStats.recipes"
        anchor-link="recipes"
      />

      <block-grid
        :blocks="itemLists.recipes"
        :custom-size="fixedSizeFullFirst"
        :force-row-size="'third'"
        :tracking="{
          listName: recipeBlockTitle,
          listLength: undefined
        }"
        @open-folder-modal="openFolderModal"
      />

      <show-more
        v-if="moreApiUrls.recipes"
        :api-url="moreApiUrls.recipes"
        :tracking="{
          event: 'recipeShowMore'
        }"
        @showMoreResult="showMoreAddToList('recipes', ...arguments)"
      />
    </section>

    <!-- RECEPTSAMLINGAR -->
    <section
      v-if="(recipeCollectionBlockTitle && itemLists.recipeCollections) || isEditable"
      ref="recipeCollections"
      class="content-area-wrapper"
    >
      <sub-heading
        v-if="recipeCollectionBlockTitle || isEditable"
        :v-edit-heading="'RecipeCollectionBlockTitle'"
        :heading="recipeCollectionBlockTitle"
        :count="itemStats.recipeCollections"
        anchor-link="recipeCollections"
      />

      <block-grid
        :blocks="itemLists.recipeCollections"
        :custom-size="fixedSizeFullFirst"
        :force-row-size="'third'"
        :tracking="{
          listName: recipeCollectionBlockTitle,
          listLength: undefined
        }"
      />

      <show-more
        v-if="moreApiUrls.recipeCollections"
        :api-url="moreApiUrls.recipeCollections"
        :tracking="{
          event: 'recipeCollectionShowMore'
        }"
        @showMoreResult="showMoreAddToList('recipeCollections', ...arguments)"
      />
    </section>
    <ica-add-single-recipe-to-folder-modal
      v-if="$root.isLoggedIn"
      :active="showFolderModal"
      :recipe-id="activeRecipeId"
      :recipe-name="activeRecipeTitle"
      :get-access-token-fn="getPublicAccessToken"
      :recipe-base-url="$root.envConfig.recipeExperienceApiBaseUrl"
      @save-to-folder="saveToFolder"
      @close-modal="closeModal"
    />
  </div>
</template>

<script>
  import pageWithNavigation from '@/vue/mixins/pageWithNavigation'
  import showMore from '@/vue/components/common/showMore'
  import showMoreMixin from '@/vue/mixins/showMoreMixin'
  import searchBox from '@/vue/components/common/searchBox'
  import recipeModalMixin from '@/vue/mixins/recipeModalMixin'

  export default {
    name: "EntrancePage",
    components: {
      searchBox,
      showMore
    },
    mixins: [pageWithNavigation, showMoreMixin, recipeModalMixin],
    props: {
      breadcrumbs: {
        type: Array,
        default: () => []
      },
      heroBlock: {
        type: Object,
        default: () => {}
      },
      matchingTagsTitle: {
        type: String,
        default: ''
      },
      displayImagesInNavigationBlocks: {
        type: Boolean,
        default: false
      },
      matchingTags: {
        type: Array,
        default: () => []
      },
      matchingTagsNavigations: {
        type: Array,
        default: () => []
      },
      contentAreaTitle: {
        type: String,
        default: 'Våra bästa tips'
      },
      contentArea: {
        type: Array,
        default: () => []
      },
      articleBlockTitle:{
        type: String,
        default: 'Artiklar'
      },
      automatedArticleArea: {
        type: Array,
        default: () => []
      },
      recipeCollectionBlockTitle: {
        type: String,
        default: 'Receptsamlingar'
      },
      automatedRecipeCollectionArea: {
        type: Array,
        default: () => []
      },
      recipeBlockTitle: {
        type: String,
        default: 'Recept'
      },
      automatedRecipeArea: {
        type: Array,
        default: () => []
      },
      searchFieldTitle: {
        type: String,
        default: 'Sök på ICA.se'
      },
      // ShowMore-mixin contains important props!
    },
    data () {
      return {
        hasSearched: false,
        fixedSizeFullFirst: {
          isSmallViewport: {
            size: "full"
          }
        },
        fixedSizeFullFirstAllSizes: {
          isSmallViewport: {
            size: "full",
          },
          isMediumViewport: {
            size: "full",
            firstOnly: true
          },
          isLargeViewport: {
            size: "full",
            firstOnly: true
          }
        },
      }
    },
    methods: {
      showSearchResult (searchData = {}) {
        this.hasSearched = true

        // To trigger in view-component to register changes on page. Otherwise it will look blank until we manually scroll
        //document.body.dispatchEvent(new CustomEvent('scroll')) fixme: see if we can trigger in view with dispatch event instead of actually scrolling 1 pixel
        window.scrollTo(window.scrollX, window.scrollY + 1)

        if (!searchData) {
          this.itemStats = {}
          return
        }

        const lists = ['articles', 'recipes', 'recipeCollections']
        lists.forEach(list => {
          this.itemLists[list] = (searchData.results && searchData.results[list]) ? searchData.results[list] : null
          this.itemStats[list] = (searchData.stats && searchData.stats[list]) ? searchData.stats[list].totalHits : null
          this.moreApiUrls[list] = (searchData.moreLinks && searchData.moreLinks[list]) ? searchData.moreLinks[list] : null
        })
      }
    }
  }
</script>
